import { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { BrowserRouter, Routes, Route, useParams, Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, Check, ChevronRight, CircleUserRound, Clock3, Copy, Crown, DollarSign, Download, Gift, Link2, LogOut, Megaphone, Menu, MessageCircle, Plus, Send, Share2, ShieldCheck, Star, Trash2, Trophy, Users, Wrench, X, Zap, KeyRound, Wallet, Settings as SettingsIcon, ClipboardList, Award, Sparkles, History } from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://customer-assets-rejwkqb3.emergentagent.net/job_padel-hub-26/artifacts/lps5mgvv_Screenshot_20260921_194104_Google.jpg";
const money = (n) => `Rp ${new Intl.NumberFormat("id-ID").format(n || 0)}`;
const todayISO = () => new Date().toISOString().slice(0, 10);
const CAPACITIES = [
  { value: 4,  courts: 1, duration: 90,  label: "4 pemain · 1 court · 90 menit" },
  { value: 6,  courts: 1, duration: 120, label: "6 pemain · 1 court · 2 jam" },
  { value: 8,  courts: 1, duration: 120, label: "8 pemain · 1 court · 2 jam" },
  { value: 10, courts: 2, duration: 120, label: "10 pemain · 2 court · 2 jam" },
  { value: 14, courts: 3, duration: 120, label: "14 pemain · 3 court · 2 jam" },
];
const LEVELS = ["Friendly", "Lower Bronze", "Bronze", "Silver"];
const FORMATS = ["Americano", "Mexicano"];
const SCORES = ["Best of 4", "Rice to 4", "Rice to 6"];

const authHeader = () => {
  const t = localStorage.getItem("cassia_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function App() {
  return <BrowserRouter><Routes>
    <Route path="/share/leaderboard/:month" element={<LeaderboardShare />} />
    <Route path="/share/leaderboard" element={<LeaderboardShare />} />
    <Route path="*" element={<CassiaFreedom />} />
  </Routes></BrowserRouter>;
}

function CassiaFreedom() {
  const [tab, setTab] = useState("home");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [ads, setAds] = useState([]);
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState({ whatsapp_admin: "6281917299100", whatsapp_group: "", guest_price: 140000, total_courts: 3 });
  const [profile, setProfile] = useState({ name: "", phone: "", membership: "Player" });
  const [authUser, setAuthUser] = useState(null);
  const [champsBanner, setChampsBanner] = useState(null); // {month, champions}
  const [showLogin, setShowLogin] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showRoom, setShowRoom] = useState(false);
  const [notifyRoom, setNotifyRoom] = useState(null);
  const [toast, setToast] = useState("");

  const showToast = (m) => setToast(m);

  const loadPublic = async () => {
    try {
      const [r, k, a, p, s, c] = await Promise.all([
        axios.get(`${API}/rooms`),
        axios.get(`${API}/rankings`),
        axios.get(`${API}/ads`),
        axios.get(`${API}/plans`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/rankings/last-month-champions`),
      ]);
      setRooms(r.data); setRankings(k.data); setAds(a.data); setPlans(p.data); setSettings(s.data);
      const dismissed = localStorage.getItem("cassia_champs_dismissed");
      if (c.data.champions?.length && dismissed !== c.data.month) setChampsBanner(c.data);
    } catch (e) { console.error(e); }
  };

  const dismissChamps = () => {
    if (champsBanner) localStorage.setItem("cassia_champs_dismissed", champsBanner.month);
    setChampsBanner(null);
  };

  const restoreSession = async () => {
    if (!localStorage.getItem("cassia_token")) return;
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: authHeader() });
      setAuthUser(data);
    } catch { localStorage.removeItem("cassia_token"); }
  };

  useEffect(() => { loadPublic(); restoreSession(); }, []);
  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(""), 2800); return () => clearTimeout(t); } }, [toast]);

  const login = async (email, password, mode = "staff") => {
    const endpoint = mode === "member" ? "member-login" : "login";
    const { data } = await axios.post(`${API}/auth/${endpoint}`, { email, password });
    localStorage.setItem("cassia_token", data.token);
    setAuthUser(data.user);
    setShowLogin(false);
    if (data.user.role === "member") {
      setTab("member_portal");
    } else if (pendingTab) {
      if (pendingTab === "funder" && data.user.role !== "funder") {
        // stay
      } else {
        setTab(pendingTab);
      }
    }
    setPendingTab(null);
    showToast(`Selamat datang, ${data.user.name}`);
  };
  const logout = () => { localStorage.removeItem("cassia_token"); setAuthUser(null); setTab("home"); showToast("Kamu sudah logout"); };

  const goStaffTab = (target) => {
    if (!authUser) { setPendingTab(target); setShowLogin(true); return; }
    if (target === "funder" && authUser.role !== "funder") { showToast("Hanya Funder yang bisa masuk"); return; }
    setTab(target); setMobileOpen(false);
  };

  const createRoom = async (form) => {
    try {
      await axios.post(`${API}/rooms`, form);
      await loadPublic();
      setShowRoom(false);
      showToast("Room berhasil dibuka");
    } catch (e) {
      showToast(e.response?.data?.detail || "Gagal membuka room");
    }
  };

  const joinRoom = async (room) => {
    if (!profile.name) { setShowProfile(true); showToast("Isi profil dulu untuk bergabung"); return; }
    const isGuest = profile.membership === "Guest";
    try {
      await axios.post(`${API}/rooms/${room.id}/join`, { player_name: profile.name, is_guest: isGuest, phone: profile.phone });
      await loadPublic();
      if (isGuest) {
        const text = `Halo admin Cassia Freedom, saya guest ${profile.name} baru join room ${room.host_name} (${room.date} ${room.time}). Mohon info pembayaran Rp ${settings.guest_price?.toLocaleString("id-ID")}.`;
        window.open(`https://wa.me/${settings.whatsapp_admin}?text=${encodeURIComponent(text)}`, "_blank");
      }
      showToast(isGuest ? "Guest terdaftar · lanjut konfirmasi WA" : "Kamu berhasil masuk room");
    } catch (e) {
      showToast(e.response?.data?.detail || "Room tidak tersedia");
    }
  };

  const confirmCourt = async (r) => {
    try {
      await axios.patch(`${API}/rooms/${r.id}/confirm`, {}, { headers: authHeader() });
      await loadPublic();
      showToast("Court dikonfirmasi · siap notify pemain");
      setNotifyRoom({ ...r, court_confirmed: true });
    } catch { showToast("Gagal konfirmasi"); }
  };

  const removeRoom = async (r) => {
    if (!window.confirm(`Hapus room ${r.host_name}?`)) return;
    try {
      await axios.delete(`${API}/rooms/${r.id}`, { headers: authHeader() });
      await loadPublic(); showToast("Room dihapus");
    } catch { showToast("Gagal hapus room"); }
  };

  const whatsapp = (text) => window.open(`https://wa.me/${settings.whatsapp_admin}?text=${encodeURIComponent(text)}`, "_blank");
  const openGroup = () => settings.whatsapp_group && window.open(settings.whatsapp_group, "_blank");

  return (
    <div className="app-shell">
      <aside className={mobileOpen ? "sidebar mobile-open" : "sidebar"}>
        <div className="brand">
          <img src={LOGO} alt="Cassia" className="brand-logo" />
          <div><b>CASSIA<br />FREEDOM</b><span>PLAY FREEDOM</span></div>
        </div>
        <div className="side-label">MENU</div>
        <Nav active={tab} setTab={(n) => { setTab(n); setMobileOpen(false); }} onStaff={goStaffTab} authUser={authUser} />
        <div className="sidebar-bottom">
          <div className="mini-court" onClick={openGroup} role="button" data-testid="wa-group-widget">
            <span>JOIN GROUP<br /><strong>WHATSAPP</strong></span><ArrowUpRight size={18} />
          </div>
          {authUser ? (
            <button className="profile-mini" data-testid="sidebar-logout-button" onClick={logout}>
              <ShieldCheck size={20} /><span>{authUser.name}<small>{authUser.role.toUpperCase()} · logout</small></span><LogOut size={16} />
            </button>
          ) : (
            <button className="profile-mini" data-testid="sidebar-profile-button" onClick={() => setShowProfile(true)}>
              <CircleUserRound size={20} /><span>{profile.name || "Buat profil"}<small>{profile.membership} member</small></span><ChevronRight size={16} />
            </button>
          )}
        </div>
      </aside>
      <main className="main">
        <header className="topbar">
          <button className="mobile-menu" data-testid="mobile-menu-button" onClick={() => setMobileOpen(!mobileOpen)}><Menu size={22} /></button>
          <div className="crumb">CASSIA FREEDOM <span>/</span> {tab.toUpperCase().replace("_", " ")}</div>
          <div className="top-actions">
            {!authUser && <button className="icon-button" data-testid="header-login-button" onClick={() => setShowLogin(true)}><KeyRound size={18} /></button>}
            <button className="icon-button" data-testid="notifications-button"><Megaphone size={18} /></button>
            <button className="avatar" data-testid="header-profile-button" onClick={() => setShowProfile(true)}>{profile.name ? profile.name.slice(0, 2).toUpperCase() : "GU"}</button>
          </div>
        </header>

        {tab === "home" && <Home rooms={rooms} ads={ads} onCreate={() => setShowRoom(true)} onJoin={joinRoom} onTab={setTab} profile={profile} champs={champsBanner} dismissChamps={dismissChamps} />}
        {tab === "rooms" && <Rooms rooms={rooms} onCreate={() => setShowRoom(true)} onJoin={joinRoom} />}
        {tab === "ranking" && <Ranking rankings={rankings} champs={champsBanner} />}
        {tab === "membership" && <Membership plans={plans} whatsapp={whatsapp} guest_price={settings.guest_price} />}
        {tab === "admin" && authUser && ["admin","funder"].includes(authUser.role) && <AdminDashboard rooms={rooms} ads={ads} settings={settings} onConfirm={confirmCourt} onRemove={removeRoom} onReload={loadPublic} showToast={showToast} authUser={authUser} onNotify={setNotifyRoom} />}
        {tab === "funder" && authUser?.role === "funder" && <FunderDashboard plans={plans} onReload={loadPublic} showToast={showToast} />}
        {tab === "member_portal" && authUser?.role === "member" && <MemberPortal whatsapp={whatsapp} showToast={showToast} />}
      </main>

      {showLogin && <LoginModal close={() => { setShowLogin(false); setPendingTab(null); }} onLogin={login} />}
      {showProfile && <ProfileModal profile={profile} setProfile={setProfile} close={() => setShowProfile(false)} save={() => { setShowProfile(false); showToast("Profil tersimpan"); }} />}
      {showRoom && <RoomModal profile={profile} close={() => setShowRoom(false)} submit={createRoom} />}
      {notifyRoom && <NotifyModal room={notifyRoom} settings={settings} close={() => setNotifyRoom(null)} showToast={showToast} />}
      {toast && <div className="toast" data-testid="toast-message"><Check size={17} />{toast}</div>}
    </div>
  );
}

function Nav({ active, setTab, onStaff, authUser }) {
  if (authUser?.role === "member") {
    const items = [
      ["member_portal", "My stats", Award],
      ["rooms", "Play rooms", Users],
      ["ranking", "Ranking", Trophy],
      ["membership", "Membership", Crown],
    ];
    return (
      <nav className="nav">
        {items.map(([id, label, Icon]) => (
          <button key={id} className={active === id ? "active" : ""} data-testid={`nav-${id}-button`} onClick={() => setTab(id)}>
            <Icon size={18} /><span>{label}</span>
          </button>
        ))}
      </nav>
    );
  }
  const items = [
    ["home", "Overview", Zap],
    ["rooms", "Play rooms", Users],
    ["ranking", "Ranking", Trophy],
    ["membership", "Membership", Crown],
  ];
  const staffItems = [["admin", "Admin desk", ShieldCheck]];
  if (authUser?.role === "funder" || !authUser) staffItems.push(["funder", "Funder vault", KeyRound]);

  return (
    <nav className="nav">
      {items.map(([id, label, Icon]) => (
        <button key={id} className={active === id ? "active" : ""} data-testid={`nav-${id}-button`} onClick={() => setTab(id)}>
          <Icon size={18} /><span>{label}</span>{id === "rooms" && <i>live</i>}
        </button>
      ))}
      {staffItems.map(([id, label, Icon]) => (
        <button key={id} className={active === id ? "active" : ""} data-testid={`nav-${id}-button`} onClick={() => onStaff(id)}>
          <Icon size={18} /><span>{label}</span>{!authUser && <i>lock</i>}
        </button>
      ))}
    </nav>
  );
}

function Home({ rooms, ads, onCreate, onJoin, onTab, profile, champs, dismissChamps }) {
  const featured = rooms.slice(0, 4);
  return (
    <div className="content">
      {champs && <ChampsBanner champs={champs} onDismiss={dismissChamps} onTab={onTab} />}
      <section className="hero">
        <div className="hero-copy">
          <span className="eyebrow"><span className="live-dot" /> CASSIA PADEL · MEMBER SESSION</span>
          <h1>Play more.<br /><em>Live free.</em></h1>
          <p>Cassia Freedom — bermain padel tanpa batas, buka room-mu, undang partner, dan naik ranking bulanan.</p>
          <div className="hero-actions">
            <button className="primary" data-testid="hero-open-room-button" onClick={onCreate}><Plus size={18} /> Buka play room</button>
            <button className="text-button" data-testid="hero-ranking-button" onClick={() => onTab("ranking")}>Lihat ranking <ArrowUpRight size={16} /></button>
          </div>
        </div>
        <div className="hero-art">
          <img src="https://images.unsplash.com/photo-1612534847738-b3af9bc31f0c?auto=format&fit=crop&w=1000&q=85" alt="Padel court" />
          <div className="hero-note"><span>UP NEXT</span><b>Open play<br />session</b><small>Hari ini · 19:30</small></div>
        </div>
      </section>

      <section className="section-head">
        <div><span className="eyebrow">QUICK ACCESS</span><h2>Your playbook</h2></div>
        <button className="text-button" data-testid="see-all-rooms-button" onClick={() => onTab("rooms")}>See all rooms <ArrowUpRight size={16} /></button>
      </section>
      <div className="quick-grid">
        <div className="stat-strip">
          <div><small>ACTIVE MEMBER</small><strong>{profile.name ? "READY TO PLAY" : "SET UP PROFILE"}</strong><span>{profile.name ? `${profile.membership} member · Cassia Freedom` : "Buat profil untuk mulai"}</span></div>
          <CircleUserRound size={32} />
        </div>
        <div className="stat-strip cyan">
          <div><small>MONTHLY LEADER</small><strong>TOP 3 EXCLUSIVE</strong><span>Hadiah setiap bulan · main lebih banyak</span></div>
          <Trophy size={32} />
        </div>
      </div>

      <section className="section-head rooms-head">
        <div><span className="eyebrow">FIND YOUR GAME</span><h2>Open play rooms</h2></div>
        <button className="primary small" data-testid="home-open-room-button" onClick={onCreate}><Plus size={16} /> Open room</button>
      </section>
      <div className="room-grid">{featured.length ? featured.map(r => <RoomCard key={r.id} room={r} onJoin={onJoin} />) : <p className="empty">Belum ada room. Jadilah yang pertama buka sesi hari ini.</p>}</div>
      {ads[0] && <AdBanner ad={ads[0]} />}
    </div>
  );
}

function RoomCard({ room, onJoin }) {
  const date = new Date(room.date + "T12:00:00");
  const players = room.players || [];
  const cap = room.capacity || 4;
  return (
    <article className="room-card" data-testid={`room-card-${room.id}`}>
      <div className="room-top">
        <span className={`level level-${(room.level || "friendly").toLowerCase().replace(" ", "-")}`}>{room.level}</span>
        <span className={room.court_confirmed ? "confirmed" : "pending"}>
          {room.court_confirmed ? <><Check size={13} /> Court confirmed</> : "Court pending"}
        </span>
      </div>
      <div className="room-date">
        <CalendarDays size={17} />
        <b>{date.toLocaleDateString("id-ID", { weekday: "short", month: "short", day: "numeric" })}</b>
        <span><Clock3 size={14} /> {room.time} · {room.duration || 90} min</span>
      </div>
      <div className="room-meta">
        <span className="chip">{room.format || "Americano"}</span>
        <span className="chip">{room.scoring || "Best of 4"}</span>
        <span className="chip">{room.courts_needed || 1} court</span>
      </div>
      <div className="room-host">
        <div className="face">{(room.host_name || "?").slice(0, 1)}</div>
        <span>Hosted by <b>{room.host_name}</b></span>
      </div>
      <div className="slots">
        <span><Users size={15} />{players.length}/{cap} players</span>
        <div className="slotbar"><i style={{ width: `${(players.length / cap) * 100}%` }} /></div>
      </div>
      <button className="room-button" data-testid={`join-room-${room.id}-button`} disabled={players.length >= cap} onClick={() => onJoin(room)}>
        {players.length >= cap ? "Room full" : "Join this room"}<ChevronRight size={16} />
      </button>
    </article>
  );
}

function Rooms({ rooms, onCreate, onJoin }) {
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(() => {
    if (filter === "all") return rooms;
    if (filter === "today") return rooms.filter(r => r.date === todayISO());
    return rooms.filter(r => (r.level || "").toLowerCase() === filter);
  }, [rooms, filter]);
  return (
    <div className="content">
      <div className="page-intro">
        <div><span className="eyebrow">PLAY TOGETHER</span><h1>Play rooms</h1><p>Semua level. Semua format. Semua durasi dari 90 menit sampai 2 jam.</p></div>
        <button className="primary" data-testid="rooms-open-room-button" onClick={onCreate}><Plus size={18} /> Buka play room</button>
      </div>
      <div className="filters">
        {[["all", `All (${rooms.length})`], ["today", "Today"], ["friendly", "Friendly"], ["silver", "Silver"]].map(([k, l]) => (
          <button key={k} className={`filter ${filter === k ? "active" : ""}`} data-testid={`filter-${k}-button`} onClick={() => setFilter(k)}>{l}</button>
        ))}
      </div>
      <div className="room-grid all">{filtered.length ? filtered.map(r => <RoomCard key={r.id} room={r} onJoin={onJoin} />) : <p className="empty">Tidak ada room untuk filter ini.</p>}</div>
    </div>
  );
}

function Ranking({ rankings, champs }) {
  const shareMonth = new Date().toISOString().slice(0, 7);
  return (
    <div className="content">
      {champs && <ChampsBanner champs={champs} onDismiss={() => {}} compact />}
      <div className="page-intro">
        <div><span className="eyebrow">SEASON RANKING</span><h1>Club ranking</h1><p>Menang lebih banyak, naik lebih tinggi. Top 3 dapat hadiah exclusive setiap bulan.</p></div>
        <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
          <Link to={`/share/leaderboard/${shareMonth}`} target="_blank" className="secondary tiny" data-testid="open-share-leaderboard" style={{textDecoration:"none"}}>
            <Share2 size={13} /> Share leaderboard
          </Link>
          <div className="champion-badge"><Trophy size={20} /> TOP 3 <span>EXCLUSIVE PRIZES</span></div>
        </div>
      </div>
      <div className="ranking-layout">
        <div className="ranking-table">
          <div className="table-head"><span>RANK</span><span>PLAYER</span><span>LEVEL</span><span>GAMES</span><span>W / L</span></div>
          {rankings.map((r, i) => (
            <div className={`rank-row rank-${i + 1}`} key={r.id} data-testid={`ranking-row-${i + 1}`}>
              <strong className="rank-no">0{i + 1}</strong>
              <div className="rank-player"><div className="face">{r.name.slice(0, 1)}</div><b>{r.name}</b></div>
              <span className="level-text">{r.level}</span>
              <b>{r.games}</b>
              <span><b>{r.wins}</b> / {r.losses}</span>
              {i === 0 && <Crown className="rank-crown" size={18} />}
            </div>
          ))}
        </div>
        <div className="prize-panel">
          <span className="eyebrow">THIS MONTH</span>
          <h3>Make your<br /><em>move.</em></h3>
          <p>Setiap game membawa kamu lebih dekat ke podium Cassia.</p>
          <div className="podium"><b>1</b><b>2</b><b>3</b></div>
        </div>
      </div>
    </div>
  );
}

function Membership({ plans, whatsapp, guest_price }) {
  const displayPlans = plans.length ? plans : [];
  return (
    <div className="content">
      <div className="page-intro">
        <div><span className="eyebrow">CHOOSE YOUR STATUS</span><h1>Membership</h1><p>Satu membership. Permainan tanpa batas.</p></div>
        <button className="text-button" data-testid="membership-whatsapp-button" onClick={() => whatsapp("Halo Cassia Freedom, saya ingin mendaftar membership.")}>
          Tanya admin via WhatsApp <ArrowUpRight size={16} />
        </button>
      </div>
      <div className="plan-grid">
        {displayPlans.map((p, i) => (
          <article className={`plan ${p.id === "reguler" ? "featured" : ""}`} key={p.id} data-testid={`membership-plan-${p.id}`}>
            <div className="plan-head">
              <span>{p.id === "star" ? <Crown size={18} /> : <Zap size={18} />} {p.name} member</span>
              {p.id === "reguler" && <b>POPULAR</b>}
            </div>
            <h2>{money(p.price)}<small>/bulan</small></h2>
            {p.next_price && <span className="next-price">Harga baru berlaku billing berikutnya: {money(p.next_price)}</span>}
            <ul>{p.benefits.map(x => <li key={x}><Check size={15} />{x}</li>)}</ul>
            <button className={p.id === "reguler" ? "primary" : "secondary"} data-testid={`membership-${p.id}-button`} onClick={() => whatsapp(`Halo Cassia Freedom, saya tertarik membership ${p.name} ${money(p.price)}/bulan.`)}>
              Pilih {p.name} <ArrowUpRight size={16} />
            </button>
          </article>
        ))}
      </div>
      <div className="guest-note">
        <Users size={22} />
        <div><b>Guest play</b><span>Bawa teman non-member bermain bersama · {money(guest_price)} / sesi</span></div>
        <button className="text-button" data-testid="guest-play-whatsapp-button" onClick={() => whatsapp(`Halo Cassia Freedom, saya ingin guest play ${money(guest_price)}.`)}>
          Konfirmasi <ArrowUpRight size={15} />
        </button>
      </div>
    </div>
  );
}

function AdBanner({ ad }) {
  return (
    <div className="ad-banner">
      <div><span className="eyebrow">CASSIA FREEDOM PRESENTS</span><h3>{ad.title}</h3><p>{ad.subtitle}</p></div>
      <button className="primary" data-testid={`ad-cta-${ad.id}-button`}>{ad.cta} <ArrowUpRight size={16} /></button>
    </div>
  );
}

// ---------------- STAFF DASHBOARDS ----------------
function AdminDashboard({ rooms, ads, settings, onConfirm, onRemove, onReload, showToast, authUser, onNotify }) {
  const [section, setSection] = useState("overview");
  const [members, setMembers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [guestPayments, setGuestPayments] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [summary, setSummary] = useState({ members: 0, revenue: 0, rooms: 0, active_members: 0, pending_confirmations: 0 });
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [showAdModal, setShowAdModal] = useState(false);
  const [resultRoom, setResultRoom] = useState(null);
  const [exportMonth, setExportMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [calDate, setCalDate] = useState(todayISO());

  const load = async () => {
    try {
      const [m, p, s, g, e] = await Promise.all([
        axios.get(`${API}/members`, { headers: authHeader() }),
        axios.get(`${API}/payments`, { headers: authHeader() }),
        axios.get(`${API}/admin/summary`, { headers: authHeader() }),
        axios.get(`${API}/guest-payments`, { headers: authHeader() }),
        axios.get(`${API}/members/expiring?days=3`, { headers: authHeader() }),
      ]);
      setMembers(m.data); setPayments(p.data); setSummary(s.data); setGuestPayments(g.data); setExpiring(e.data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const saveMember = async (form) => {
    try {
      if (editingMember) {
        await axios.patch(`${API}/members/${editingMember.id}`, form, { headers: authHeader() });
        showToast("Member diperbarui");
      } else {
        await axios.post(`${API}/members`, form, { headers: authHeader() });
        showToast("Member ditambahkan");
      }
      setShowMemberModal(false); setEditingMember(null); load();
    } catch (e) { showToast(e.response?.data?.detail || "Gagal simpan"); }
  };

  const delMember = async (m) => {
    if (!window.confirm(`Hapus member ${m.name}?`)) return;
    await axios.delete(`${API}/members/${m.id}`, { headers: authHeader() });
    showToast("Member dihapus"); load();
  };

  const logPayment = async (form) => {
    try {
      await axios.post(`${API}/payments`, form, { headers: authHeader() });
      showToast("Pembayaran tercatat"); setShowPaymentModal(null); load();
    } catch (e) { showToast(e.response?.data?.detail || "Gagal simpan"); }
  };

  const saveAd = async (form) => {
    try {
      await axios.post(`${API}/ads`, form, { headers: authHeader() });
      showToast("Iklan ditambahkan"); setShowAdModal(false); onReload();
    } catch (e) { showToast("Gagal tambah iklan"); }
  };

  const delAd = async (a) => {
    await axios.delete(`${API}/ads/${a.id}`, { headers: authHeader() });
    showToast("Iklan dihapus"); onReload();
  };

  const submitResult = async (payload) => {
    try {
      await axios.post(`${API}/rooms/${resultRoom.id}/result`, payload, { headers: authHeader() });
      showToast(`Hasil dicatat${payload.mvp ? " · MVP " + payload.mvp : ""}`);
      setResultRoom(null); onReload(); load();
    } catch (e) { showToast(e.response?.data?.detail || "Gagal simpan hasil"); }
  };

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/payments/export${exportMonth ? `?month=${exportMonth}` : ""}`, {
        headers: authHeader(), responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `cassia-payments-${exportMonth || "all"}.csv`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      showToast("CSV terunduh");
    } catch (e) { showToast("Gagal unduh CSV"); }
  };

  const sendReminder = (m) => {
    const text = `Halo ${m.name}, ini pengingat dari Cassia Freedom — masa aktif membership ${m.plan?.toUpperCase()} kamu berakhir ${m.period_end} (${m.days_left >= 0 ? m.days_left + " hari lagi" : "sudah expired"}). Silakan konfirmasi perpanjangan ke admin 6281917299100. Terima kasih!`;
    window.open(`https://wa.me/${m.phone?.replace(/^0/, "62")}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const markGuestPaid = async (g, paid) => {
    try {
      await axios.patch(`${API}/guest-payments/${g.id}`, { paid }, { headers: authHeader() });
      showToast(paid ? "Guest tercatat lunas" : "Status diubah"); load();
    } catch { showToast("Gagal update guest"); }
  };
  const delGuest = async (g) => {
    if (!window.confirm(`Hapus guest ${g.guest_name}?`)) return;
    await axios.delete(`${API}/guest-payments/${g.id}`, { headers: authHeader() });
    showToast("Guest dihapus"); load();
  };
  const waGuest = (g) => {
    const text = `Halo ${g.guest_name}, konfirmasi pembayaran guest play Cassia Freedom sebesar ${money(g.amount)} untuk sesi ${g.room_label || "yang lalu"}. Terima kasih!`;
    window.open(`https://wa.me/${(g.phone || settings.whatsapp_admin || "").replace(/^0/, "62")}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div className="content">
      <div className="page-intro">
        <div><span className="eyebrow">CONTROL CENTER</span><h1>Admin desk</h1><p>Kelola member, court, pembayaran, dan iklan Cassia Freedom.</p></div>
        <span className="admin-status"><span className="live-dot" /> {authUser.role.toUpperCase()} · {authUser.email}</span>
      </div>

      <div className="admin-tabs">
        {[["overview", ClipboardList], ["members", Users], ["payments", Wallet], ["rooms", CalendarDays], ["calendar", CalendarDays], ["maintenance", Wrench], ["guests", Users], ["ads", Megaphone], ["settings", SettingsIcon]].map(([k, I]) => (
          <button key={k} className={section === k ? "active" : ""} data-testid={`admin-tab-${k}`} onClick={() => setSection(k)}>
            <I size={15} /> {k}{k === "guests" && guestPayments.filter(x=>!x.paid).length ? <em className="tab-badge">{guestPayments.filter(x=>!x.paid).length}</em> : null}{k === "members" && expiring.length ? <em className="tab-badge warn">{expiring.length}</em> : null}
          </button>
        ))}
      </div>

      {section === "overview" && (
        <>
          <div className="admin-stats">
            <div><small>ACTIVE MEMBERS</small><b>{summary.active_members}</b><span>{summary.members} total</span></div>
            <div><small>ROOMS TERCATAT</small><b>{summary.rooms}</b><span>{summary.full_rooms} full</span></div>
            <div><small>PENDING COURT</small><b>{String(summary.pending_confirmations).padStart(2, "0")}</b><span>Perlu konfirmasi</span></div>
            <div><small>REVENUE</small><b>{money(summary.revenue).replace("Rp ", "Rp ")}</b><span>{summary.payments_count} transaksi</span></div>
          </div>
        </>
      )}

      {section === "members" && (
        <div className="admin-section">
          <div className="section-head"><div><span className="eyebrow">MEMBER BASE</span><h2>Kelola member</h2></div>
            <button className="secondary tiny" data-testid="add-member-button" onClick={() => { setEditingMember(null); setShowMemberModal(true); }}><Plus size={15} /> Tambah member</button>
          </div>
          {expiring.length > 0 && (
            <div className="renewal-banner" data-testid="renewal-banner">
              <div><b>⏰ {expiring.length} membership perlu diperpanjang</b><span>Kirim reminder WhatsApp otomatis ke member yang masa aktifnya ≤ 3 hari.</span></div>
              <div className="renewal-list">
                {expiring.map(m => (
                  <div className="renewal-row" key={m.id} data-testid={`renewal-row-${m.id}`}>
                    <div>
                      <b>{m.name}</b>
                      <span>{m.plan?.toUpperCase()} · {m.expired ? "EXPIRED" : `${m.days_left} hari lagi`} · until {m.period_end}</span>
                    </div>
                    <button className="secondary tiny" data-testid={`remind-${m.id}-button`} disabled={!m.phone} onClick={() => sendReminder(m)}>
                      <ArrowUpRight size={12} /> Kirim reminder
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="member-table">
            <div className="mt-head"><span>NAMA</span><span>PLAN</span><span>PHONE</span><span>PERIOD</span><span>STATUS</span><span>ACTION</span></div>
            {members.length ? members.map(m => (
              <div className="mt-row" key={m.id} data-testid={`member-row-${m.id}`}>
                <b>{m.name}</b>
                <span className={`plan-tag plan-${m.plan}`}>{m.plan}</span>
                <span>{m.phone}</span>
                <span>{m.period_end || "-"}</span>
                <span className={m.active ? "confirmed" : "pending"}>{m.active ? "AKTIF" : "NONAKTIF"}</span>
                <span className="mt-actions">
                  <button className="tiny secondary" data-testid={`edit-member-${m.id}`} onClick={() => { setEditingMember(m); setShowMemberModal(true); }}>Edit</button>
                  <button className="tiny secondary" data-testid={`pay-member-${m.id}`} onClick={() => setShowPaymentModal(m)}><DollarSign size={12} /> Bayar</button>
                  <button className="tiny icon-danger" data-testid={`del-member-${m.id}`} onClick={() => delMember(m)}><Trash2 size={13} /></button>
                </span>
              </div>
            )) : <p className="empty">Belum ada member. Tambahkan lewat tombol di atas.</p>}
          </div>
        </div>
      )}

      {section === "calendar" && (
        <CourtCalendar rooms={rooms} settings={settings} date={calDate} setDate={setCalDate} onConfirm={onConfirm} />
      )}

      {section === "guests" && (
        <div className="admin-section">
          <div className="section-head"><div><span className="eyebrow">GUEST TRACKER</span><h2>Pembayaran guest play</h2></div>
            <span className="pending-pill">{guestPayments.filter(g => !g.paid).length} belum lunas</span>
          </div>
          <div className="member-table">
            <div className="mt-head six"><span>NAMA GUEST</span><span>ROOM</span><span>AMOUNT</span><span>PHONE</span><span>STATUS</span><span>ACTION</span></div>
            {guestPayments.length ? guestPayments.map(g => (
              <div className="mt-row six" key={g.id} data-testid={`guest-row-${g.id}`}>
                <b>{g.guest_name}</b>
                <span>{g.room_label || "-"}</span>
                <b>{money(g.amount)}</b>
                <span>{g.phone || "-"}</span>
                <span className={g.paid ? "confirmed" : "pending"}>{g.paid ? "LUNAS" : "PENDING"}</span>
                <span className="mt-actions">
                  {!g.paid && <button className="tiny secondary" data-testid={`guest-paid-${g.id}`} onClick={() => markGuestPaid(g, true)}><Check size={12} /> Tandai lunas</button>}
                  {g.paid && <button className="tiny secondary" data-testid={`guest-unpaid-${g.id}`} onClick={() => markGuestPaid(g, false)}>Batal lunas</button>}
                  <button className="tiny secondary" data-testid={`guest-wa-${g.id}`} onClick={() => waGuest(g)}><ArrowUpRight size={12} /> WA</button>
                  <button className="tiny icon-danger" data-testid={`guest-del-${g.id}`} onClick={() => delGuest(g)}><Trash2 size={13} /></button>
                </span>
              </div>
            )) : <p className="empty">Belum ada guest yang bergabung. Guest otomatis tercatat saat join room dengan status Guest.</p>}
          </div>
        </div>
      )}

      {section === "maintenance" && <MaintenancePanel showToast={showToast} />}

      {section === "payments" && (
        <div className="admin-section">
          <div className="section-head"><div><span className="eyebrow">CASH FLOW</span><h2>Riwayat pembayaran</h2></div>
            <div className="export-row">
              <input type="month" data-testid="export-month-input" value={exportMonth} onChange={e => setExportMonth(e.target.value)} />
              <button className="secondary tiny" data-testid="export-csv-button" onClick={exportCSV}><Download size={13} /> Export CSV</button>
            </div>
          </div>
          <div className="member-table">
            <div className="mt-head six"><span>TANGGAL</span><span>MEMBER</span><span>AMOUNT</span><span>METODE</span><span>PERIODE</span><span>OLEH</span></div>
            {payments.length ? payments.map(p => {
              const mem = members.find(x => x.id === p.member_id);
              return (
                <div className="mt-row six" key={p.id} data-testid={`payment-row-${p.id}`}>
                  <span>{(p.created_at || "").slice(0, 10)}</span>
                  <b>{mem?.name || p.member_id}</b>
                  <b>{money(p.amount)}</b>
                  <span>{p.method}</span>
                  <span>{p.period_start} → {p.period_end}</span>
                  <span>{p.logged_by}</span>
                </div>
              );
            }) : <p className="empty">Belum ada pembayaran tercatat.</p>}
          </div>
        </div>
      )}

      {section === "rooms" && (
        <div className="admin-section">
          <div className="section-head"><div><span className="eyebrow">COURT CONTROL</span><h2>Konfirmasi & hasil</h2></div>
            <span className="pending-pill">{rooms.filter(r => !r.court_confirmed).length} pending</span>
          </div>
          {rooms.length ? rooms.map(r => (
            <div className="admin-room" key={r.id}>
              <div className="admin-room-info">
                <div className="face">{r.host_name.slice(0, 1)}</div>
                <div>
                  <b>{r.host_name}'s room · {r.format}</b>
                  <span>{r.date} · {r.time} · {r.level} · {r.players?.length || 0}/{r.capacity} · {r.courts_needed} court · {r.scoring}</span>
                </div>
              </div>
              <span className={r.court_confirmed ? "confirmed" : "pending"}>{r.court_confirmed ? <><Check size={13} /> Confirmed</> : "Pending"}</span>
              <button className="secondary tiny" data-testid={`admin-confirm-${r.id}-button`} disabled={r.court_confirmed} onClick={() => onConfirm(r)}>
                {r.court_confirmed ? "Done" : "Confirm court"}
              </button>
              <button className="secondary tiny" data-testid={`admin-notify-${r.id}-button`} disabled={!(r.players?.length)} onClick={() => onNotify(r)}>
                <Send size={12} /> Notify
              </button>
              <button className="secondary tiny" data-testid={`admin-result-${r.id}-button`} disabled={!!r.result_recorded || !(r.players?.length)} onClick={() => setResultRoom(r)}>
                {r.result_recorded ? <><Award size={12} /> Recorded</> : <><Trophy size={12} /> Set result</>}
              </button>
              <button className="tiny icon-danger" data-testid={`admin-remove-${r.id}-button`} onClick={() => onRemove(r)}><Trash2 size={13} /></button>
            </div>
          )) : <p className="empty">Belum ada room yang dibuat.</p>}
        </div>
      )}

      {section === "ads" && (
        <div className="admin-section">
          <div className="section-head"><div><span className="eyebrow">CAMPAIGNS</span><h2>Iklan aktif</h2></div>
            <button className="secondary tiny" data-testid="create-ad-button" onClick={() => setShowAdModal(true)}><Plus size={15} /> New ad</button>
          </div>
          <div className="ad-grid">
            {ads.map(a => (
              <div className="ad-card" key={a.id} data-testid={`ad-card-${a.id}`}>
                <span className="eyebrow">CASSIA · {a.cta}</span>
                <h3>{a.title}</h3><p>{a.subtitle}</p>
                <button className="tiny icon-danger" data-testid={`del-ad-${a.id}`} onClick={() => delAd(a)}><Trash2 size={13} /> Hapus</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {section === "settings" && <SettingsPanel initial={settings} onReload={onReload} showToast={showToast} />}

      {showMemberModal && <MemberModal initial={editingMember} close={() => { setShowMemberModal(false); setEditingMember(null); }} submit={saveMember} />}
      {showPaymentModal && <PaymentModal member={showPaymentModal} plans={[]} close={() => setShowPaymentModal(null)} submit={logPayment} />}
      {showAdModal && <AdModal close={() => setShowAdModal(false)} submit={saveAd} />}
      {resultRoom && <ResultModal room={resultRoom} close={() => setResultRoom(null)} submit={submitResult} />}
    </div>
  );
}

function FunderDashboard({ plans, onReload, showToast }) {
  const [editing, setEditing] = useState(null);
  const submit = async (form) => {
    try {
      await axios.patch(`${API}/plans/${editing.id}`, form, { headers: authHeader() });
      showToast(form.apply_now ? "Harga diperbarui (berlaku sekarang)" : "Harga akan berlaku billing berikutnya");
      setEditing(null); onReload();
    } catch (e) { showToast("Gagal update harga"); }
  };
  return (
    <div className="content">
      <div className="page-intro">
        <div><span className="eyebrow">FUNDER VAULT</span><h1>Owner control</h1><p>Kelola harga membership dan strategi harga Cassia Freedom.</p></div>
        <span className="admin-status funder"><KeyRound size={14} /> FUNDER ACCESS</span>
      </div>
      <div className="plan-grid">
        {plans.map(p => (
          <article className={`plan ${p.id === "reguler" ? "featured" : ""}`} key={p.id}>
            <div className="plan-head">
              <span>{p.id === "star" ? <Crown size={18} /> : <Zap size={18} />} {p.name} member</span>
            </div>
            <h2>{money(p.price)}<small>/bulan</small></h2>
            {p.next_price && <span className="next-price">Next billing: {money(p.next_price)}</span>}
            <ul>{p.benefits.map(x => <li key={x}><Check size={15} />{x}</li>)}</ul>
            <button className="primary" data-testid={`funder-edit-${p.id}`} onClick={() => setEditing(p)}>Ubah harga <ArrowUpRight size={16} /></button>
          </article>
        ))}
      </div>
      {editing && <PriceModal plan={editing} close={() => setEditing(null)} submit={submit} />}
    </div>
  );
}

function CourtCalendar({ rooms, settings, date, setDate, onConfirm }) {
  const [dayRooms, setDayRooms] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const totalCourts = settings.total_courts || 3;
  const load = async () => {
    try {
      const r = await axios.get(`${API}/rooms/calendar/${date}`);
      setDayRooms(r.data.rooms || []);
      setMaintenance(r.data.maintenance || []);
    } catch { setDayRooms([]); setMaintenance([]); }
  };
  useEffect(() => { load(); }, [date]);
  // 30-min slots from 06:00 to 21:30
  const slots = [];
  for (let m = 6 * 60; m <= 21 * 60 + 30; m += 30) {
    const h = Math.floor(m / 60), mm = m % 60;
    slots.push(`${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
  }
  const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
  // For each slot, count courts used
  const cellsForSlot = (slot) => {
    const sMin = toMin(slot); const eMin = sMin + 30;
    const cells = [];
    let used = 0;
    for (const r of dayRooms) {
      const rs = toMin(r.time); const re = rs + (r.duration || 90);
      if (rs < eMin && re > sMin) {
        for (let c = 0; c < (r.courts_needed || 1); c++) {
          cells.push({ type: "room", room: r, courtIdx: used + c });
        }
        used += (r.courts_needed || 1);
      }
    }
    for (const b of maintenance) {
      const bs = toMin(b.time); const be = bs + (b.duration || 60);
      if (bs < eMin && be > sMin) {
        for (let c = 0; c < (b.courts || 1); c++) cells.push({ type: "maint", block: b, courtIdx: used + c });
        used += (b.courts || 1);
      }
    }
    return { cells, used };
  };
  return (
    <div className="admin-section">
      <div className="section-head">
        <div><span className="eyebrow">COURT CALENDAR</span><h2>Grid ketersediaan court</h2></div>
        <input type="date" data-testid="calendar-date-input" value={date} onChange={e => setDate(e.target.value)} />
      </div>
      <div className="cal-legend">
        <span><i className="dot open" /> Kosong</span>
        <span><i className="dot booked" /> Booked</span>
        <span><i className="dot confirmed" /> Confirmed</span>
        <span><i className="dot maint" /> Maintenance</span>
        <span className="cal-total">{totalCourts} court aktif</span>
      </div>
      <div className="calendar-grid" data-testid="court-calendar-grid">
        <div className="cal-head">
          <span className="cal-time-header">JAM</span>
          {Array.from({ length: totalCourts }, (_, i) => <span key={i}>COURT {i + 1}</span>)}
        </div>
        {slots.map(slot => {
          const { cells } = cellsForSlot(slot);
          return (
            <div className="cal-row" key={slot} data-testid={`cal-slot-${slot}`}>
              <span className="cal-time">{slot}</span>
              {Array.from({ length: totalCourts }, (_, courtIdx) => {
                const cell = cells[courtIdx];
                if (!cell) return <div className="cal-cell open" key={courtIdx} data-testid={`cal-cell-${slot}-${courtIdx}`}>—</div>;
                if (cell.type === "maint") {
                  const b = cell.block;
                  const isStart = b.time === slot;
                  return (
                    <div className="cal-cell maint" key={courtIdx} data-testid={`cal-cell-${slot}-${courtIdx}`}>
                      {isStart ? <><b><Wrench size={11} /> {b.reason}</b><small>{b.duration} min</small></> : <small className="cont">↑ {b.reason}</small>}
                    </div>
                  );
                }
                const r = cell.room;
                const isStart = r.time === slot;
                return (
                  <div className={`cal-cell ${r.court_confirmed ? "confirmed" : "booked"}`} key={courtIdx} data-testid={`cal-cell-${slot}-${courtIdx}`}>
                    {isStart && <><b>{r.host_name}</b><small>{r.level} · {r.capacity}p · {r.format}</small></>}
                    {!isStart && <small className="cont">↑ {r.host_name}</small>}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="cal-summary">
        {dayRooms.length === 0 && maintenance.length === 0 ? <span>Tidak ada booking pada {date}.</span> :
          <span>{dayRooms.length} sesi terjadwal · {maintenance.length} maintenance · {dayRooms.filter(r => !r.court_confirmed).length} pending konfirmasi</span>}
      </div>
    </div>
  );
}

function MaintenancePanel({ showToast }) {
  const [blocks, setBlocks] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const load = async () => {
    try { const r = await axios.get(`${API}/maintenance`); setBlocks(r.data); }
    catch { setBlocks([]); }
  };
  useEffect(() => { load(); }, []);
  const submit = async (form) => {
    try {
      await axios.post(`${API}/maintenance`, form, { headers: authHeader() });
      showToast("Maintenance block ditambahkan"); setShowAdd(false); load();
    } catch (e) { showToast(e.response?.data?.detail || "Gagal tambah"); }
  };
  const del = async (b) => {
    if (!window.confirm(`Hapus blok ${b.reason}?`)) return;
    await axios.delete(`${API}/maintenance/${b.id}`, { headers: authHeader() });
    showToast("Blok dihapus"); load();
  };
  return (
    <div className="admin-section">
      <div className="section-head"><div><span className="eyebrow">MAINTENANCE</span><h2>Court blocks</h2></div>
        <button className="secondary tiny" data-testid="add-maintenance-button" onClick={() => setShowAdd(true)}><Plus size={15} /> Tambah blok</button>
      </div>
      <div className="member-table">
        <div className="mt-head six"><span>TANGGAL</span><span>JAM</span><span>DURASI</span><span>COURT</span><span>ALASAN</span><span>ACTION</span></div>
        {blocks.length ? blocks.map(b => (
          <div className="mt-row six" key={b.id} data-testid={`maint-row-${b.id}`}>
            <b>{b.date}</b><span>{b.time}</span><span>{b.duration} min</span><span>{b.courts} court</span>
            <span>{b.reason}</span>
            <button className="tiny icon-danger" data-testid={`del-maint-${b.id}`} onClick={() => del(b)}><Trash2 size={13} /> Hapus</button>
          </div>
        )) : <p className="empty">Belum ada maintenance block.</p>}
      </div>
      {showAdd && <MaintenanceModal close={() => setShowAdd(false)} submit={submit} />}
    </div>
  );
}

function MaintenanceModal({ close, submit }) {
  const [form, setForm] = useState({ date: todayISO(), time: "10:00", duration: 60, courts: 1, reason: "Deep clean" });
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <button className="modal-close" data-testid="maint-modal-close" onClick={close}><X size={18} /></button>
        <span className="eyebrow">BLOCK COURT</span><h2>Tambah maintenance</h2>
        <p>Reserve court agar tidak bentrok dengan booking pemain.</p>
        <div className="grid-2">
          <label>Tanggal<input type="date" data-testid="maint-date-input" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></label>
          <label>Jam mulai<input type="time" data-testid="maint-time-input" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} /></label>
          <label>Durasi (menit)<input type="number" min="15" step="15" data-testid="maint-duration-input" value={form.duration} onChange={e => setForm({ ...form, duration: parseInt(e.target.value) || 60 })} /></label>
          <label>Court dipakai<input type="number" min="1" data-testid="maint-courts-input" value={form.courts} onChange={e => setForm({ ...form, courts: parseInt(e.target.value) || 1 })} /></label>
          <label className="span-2">Alasan<input data-testid="maint-reason-input" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Deep clean, private event, dll" /></label>
        </div>
        <button className="primary full" data-testid="maint-submit-button" onClick={() => submit(form)}>Simpan blok <Wrench size={17} /></button>
      </div>
    </div>
  );
}

// ---------------- NOTIFY / SHARE ----------------
function NotifyModal({ room, settings, close, showToast }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/rooms/${room.id}/notify-info`, { headers: authHeader() })
      .then(r => setData(r.data))
      .catch(() => setData({ room, players: (room.players || []).map(p => ({ ...p, phone: p.phone || null })) }));
  }, [room.id]);
  const buildText = (name) => `Halo ${name}, court kamu di Cassia Freedom SUDAH DIKONFIRMASI ✅\n\n📅 ${room.date} · ⏰ ${room.time} (${room.duration || 90} menit)\n🏸 ${room.host_name}'s room · ${room.level} · ${room.format || "Americano"}\n\nDitunggu di lapangan ya! — Admin Cassia`;
  const wa = (p) => {
    if (!p.phone) return showToast(`${p.name}: nomor WA belum ada`);
    const phone = p.phone.replace(/^0/, "62").replace(/[^\d]/g, "");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(buildText(p.name))}`, "_blank");
  };
  const notifyAll = () => {
    const players = data?.players || [];
    let opened = 0;
    players.forEach(p => {
      if (p.phone) { wa(p); opened++; }
    });
    showToast(opened ? `${opened} WhatsApp tab dibuka` : "Tidak ada nomor WA tersedia");
  };
  return (
    <div className="modal-backdrop">
      <div className="modal wide">
        <button className="modal-close" data-testid="notify-modal-close" onClick={close}><X size={18} /></button>
        <span className="eyebrow"><Send size={12} /> NOTIFY PLAYERS</span><h2>Kirim WA konfirmasi</h2>
        <p>Court sudah confirmed — kirim WA ke setiap pemain dengan detail sesi.</p>
        {!data ? <p className="empty">Loading...</p> : (
          <>
            <div className="notify-list">
              {data.players.map((p, idx) => (
                <div className="notify-row" key={p.name || idx} data-testid={`notify-row-${p.name || idx}`}>
                  <div className="face">{(p.name || "?").slice(0, 1)}</div>
                  <div className="notify-info">
                    <b>{p.name || "-"}{p.is_guest && <em className="tab-badge warn">guest</em>}</b>
                    <span>{p.phone || "Nomor WA belum tercatat"}</span>
                  </div>
                  <button className="secondary tiny" data-testid={`notify-send-${p.name || idx}`} disabled={!p.phone} onClick={() => wa(p)}>
                    <Send size={12} /> Kirim
                  </button>
                </div>
              ))}
            </div>
            <button className="primary full" data-testid="notify-all-button" onClick={notifyAll}>
              <Send size={16} /> Kirim ke semua pemain
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function LeaderboardShare() {
  const { month: monthParam } = useParams();
  const cardRef = useRef(null);
  const [month, setMonth] = useState(monthParam || new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [copyLabel, setCopyLabel] = useState("Copy link");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/rankings/monthly/${month}`);
      setRows(r.data.slice(0, 10));
    } catch { setRows([]); }
  };
  useEffect(() => { load(); }, [month]);

  const download = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: "#080c0e", scale: 2, useCORS: true });
      const link = document.createElement("a");
      link.download = `cassia-leaderboard-${month}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) { console.error(e); }
    finally { setDownloading(false); }
  };
  const copy = async () => {
    const url = `${window.location.origin}/share/leaderboard/${month}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopyLabel("Copied!");
      setTimeout(() => setCopyLabel("Copy link"), 2000);
    } catch { /* ignore */ }
  };
  const monthLabel = new Date(month + "-01").toLocaleDateString("id-ID", { year: "numeric", month: "long" });

  return (
    <div className="share-page">
      <div className="share-toolbar">
        <Link to="/" className="text-button" data-testid="share-back-home"><ArrowUpRight size={14} style={{ transform: "rotate(180deg)" }} /> Kembali</Link>
        <input type="month" data-testid="share-month-input" value={month} onChange={e => setMonth(e.target.value)} />
        <button className="secondary tiny" data-testid="share-copy-link" onClick={copy}><Link2 size={13} /> {copyLabel}</button>
        <button className="primary tiny" data-testid="share-download-image" disabled={downloading} onClick={download}><Download size={13} /> {downloading ? "Rendering..." : "Download PNG"}</button>
      </div>
      <div className="share-card" ref={cardRef} data-testid="share-card">
        <div className="share-header">
          <div>
            <span className="eyebrow"><Trophy size={12} /> CASSIA FREEDOM · MONTHLY LEADERBOARD</span>
            <h1>Top 10<br /><em>{monthLabel}</em></h1>
          </div>
          <img src={LOGO} alt="Cassia" className="share-logo" />
        </div>
        {rows.length === 0 ? (
          <div className="share-empty">Belum ada match tercatat untuk bulan ini.</div>
        ) : (
          <div className="share-list">
            {rows.map((r, i) => (
              <div className={`share-row rank-${i + 1}`} key={r.name} data-testid={`share-row-${i + 1}`}>
                <b className="share-pos">{String(i + 1).padStart(2, "0")}</b>
                <div className="face">{r.name.slice(0, 1)}</div>
                <div className="share-player">
                  <b>{r.name}</b>
                  <span>{r.games} games · {r.wins} wins · {r.losses} losses</span>
                </div>
                <div className="share-wins">{r.wins}<small>WINS</small></div>
                {i === 0 && <Crown size={20} className="share-crown" />}
              </div>
            ))}
          </div>
        )}
        <div className="share-footer">
          <span>Play more. Live free.</span>
          <span className="share-brand">CASSIA FREEDOM · @cassiapadel</span>
        </div>
      </div>
    </div>
  );
}

// ---------------- BANNERS ----------------
function ChampsBanner({ champs, onDismiss, onTab, compact }) {
  if (!champs.champions?.length) return null;
  const [gold, silver, bronze] = [champs.champions[0], champs.champions[1], champs.champions[2]];
  return (
    <div className={`champs-banner ${compact ? "compact" : ""}`} data-testid="champs-banner">
      <div className="champs-head">
        <span className="eyebrow"><Sparkles size={12} /> MONTHLY CHAMPIONS · {champs.month}</span>
        <h2>Top 3 <em>Cassia</em> stars</h2>
        <p>Selamat kepada juara bulan lalu — hadiah exclusive menanti di club.</p>
      </div>
      <div className="champs-podium">
        {silver && <div className="champ-card silver"><Trophy size={18} /><small>02</small><b>{silver.name}</b><span>{silver.wins} wins · {silver.games} games</span></div>}
        {gold && <div className="champ-card gold"><Crown size={22} /><small>01</small><b>{gold.name}</b><span>{gold.wins} wins · {gold.games} games</span></div>}
        {bronze && <div className="champ-card bronze"><Trophy size={16} /><small>03</small><b>{bronze.name}</b><span>{bronze.wins} wins · {bronze.games} games</span></div>}
      </div>
      {!compact && (
        <div className="champs-actions">
          <button className="text-button" data-testid="champs-see-ranking" onClick={() => { onDismiss(); onTab && onTab("ranking"); }}>Lihat ranking penuh <ArrowUpRight size={16} /></button>
          <button className="text-button subtle" data-testid="champs-dismiss" onClick={onDismiss}>Tutup</button>
        </div>
      )}
    </div>
  );
}

// ---------------- MEMBER PORTAL ----------------
function MemberPortal({ whatsapp, showToast }) {
  const [data, setData] = useState(null);
  const load = async () => {
    try {
      const r = await axios.get(`${API}/member/me/stats`, { headers: authHeader() });
      setData(r.data);
    } catch (e) { showToast("Gagal load data member"); }
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="content"><p className="empty">Loading data kamu...</p></div>;
  const { member, stats, rooms, payments, badges = [], commentary = [], referrals = [] } = data;
  const winRate = stats.games ? Math.round((stats.wins / stats.games) * 100) : 0;
  const copyReferral = async () => {
    try {
      await navigator.clipboard.writeText(member.referral_code || "");
      showToast("Kode referral tersalin");
    } catch { showToast("Gagal salin"); }
  };
  return (
    <div className="content">
      <div className="page-intro">
        <div><span className="eyebrow">MY DASHBOARD</span><h1>Hi, {member.name}</h1><p>{member.plan?.toUpperCase()} member · {member.active ? "Aktif" : "Nonaktif"} · Period end {member.period_end || "-"}</p></div>
        <button className="text-button" data-testid="member-wa-admin" onClick={() => whatsapp(`Halo admin, saya ${member.name} (${member.email}) ingin konfirmasi perpanjangan membership.`)}>
          Hubungi admin <ArrowUpRight size={16} />
        </button>
      </div>
      <div className="admin-stats">
        <div data-testid="stat-games"><small>GAMES PLAYED</small><b>{stats.games}</b><span>Total match Cassia</span></div>
        <div data-testid="stat-wins"><small>WINS</small><b>{stats.wins}</b><span>Menang tercatat</span></div>
        <div data-testid="stat-losses"><small>LOSSES</small><b>{stats.losses}</b><span>Kalah tercatat</span></div>
        <div data-testid="stat-winrate"><small>WIN RATE</small><b>{winRate}<span style={{fontSize:"0.6em",color:"#8fa09d",marginLeft:2}}>%</span></b><span>MVP {stats.mvp_count || 0}x</span></div>
      </div>

      <div className="referral-card" data-testid="referral-card">
        <div>
          <span className="eyebrow"><Gift size={12} /> REFERRAL REWARDS</span>
          <h2>Ajak teman, dapat kredit</h2>
          <p>Setiap teman yang gabung pakai kode-mu → kamu dapat 10% dari pembayaran pertamanya sebagai kredit membership.</p>
          <div className="referral-code" data-testid="referral-code-display">
            <span>KODE-MU</span>
            <b>{member.referral_code || "-"}</b>
            <button className="secondary tiny" data-testid="copy-referral-button" onClick={copyReferral}><Copy size={13} /> Copy</button>
          </div>
        </div>
        <div className="referral-stats">
          <div><small>KREDIT KAMU</small><b>{money(member.credit_balance || 0)}</b></div>
          <div><small>TEMAN DIAJAK</small><b>{referrals.length}</b></div>
        </div>
      </div>

      {commentary.length > 0 && (
        <div className="admin-section">
          <div className="section-head"><div><span className="eyebrow"><MessageCircle size={12} /> MATCH COMMENTARY</span><h2>Cerita dari lapangan</h2></div></div>
          <div className="commentary-list">
            {commentary.map(m => {
              const you = m.players.find(p => p.name.toLowerCase() === member.name.toLowerCase());
              return (
                <div className="commentary-card" key={m.id} data-testid={`commentary-${m.id}`}>
                  <div className="commentary-head">
                    <span className="eyebrow">{m.date}</span>
                    {m.mvp && <span className={`mvp-pill ${m.mvp === member.name ? "yours" : ""}`}><Star size={11} /> MVP: {m.mvp}</span>}
                    {you && <span className={you.is_winner ? "confirmed" : "pending"}>{you.is_winner ? "MENANG" : "KALAH"}{you.is_mvp && " · MVP"}</span>}
                  </div>
                  <p>"{m.commentary}"</p>
                  <small>— {m.recorded_by}</small>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="admin-section">
        <div className="section-head"><div><span className="eyebrow"><Sparkles size={12} /> ACHIEVEMENTS</span><h2>Badge kamu</h2></div><span className="pending-pill">{badges.length} unlocked</span></div>
        {badges.length ? (
          <div className="badge-grid" data-testid="badges-grid">
            {badges.map(b => (
              <div className="badge-card" key={b.id} data-testid={`badge-${b.id}`}>
                <div className="badge-emoji">{b.emoji}</div>
                <b>{b.name}</b>
                <span>{b.desc}</span>
              </div>
            ))}
          </div>
        ) : <p className="empty">Belum ada badge. Mainlah untuk membuka pencapaian pertamamu 🎯</p>}
      </div>

      <div className="admin-section">
        <div className="section-head"><div><span className="eyebrow"><History size={12} /> HISTORY</span><h2>Room yang kamu ikuti</h2></div></div>
        {rooms.length ? (
          <div className="member-table">
            <div className="mt-head six"><span>TANGGAL</span><span>JAM</span><span>HOST</span><span>FORMAT</span><span>LEVEL</span><span>STATUS</span></div>
            {rooms.map(r => (
              <div className="mt-row six" key={r.id} data-testid={`member-room-${r.id}`}>
                <b>{r.date}</b><span>{r.time}</span><span>{r.host_name}</span>
                <span>{r.format}</span><span>{r.level}</span>
                <span className={r.status === "Completed" ? "confirmed" : "pending"}>{r.status || "Open"}</span>
              </div>
            ))}
          </div>
        ) : <p className="empty">Belum ada room yang kamu ikuti.</p>}
      </div>

      <div className="admin-section">
        <div className="section-head"><div><span className="eyebrow"><Wallet size={12} /> PAYMENTS</span><h2>Riwayat pembayaranmu</h2></div></div>
        {payments.length ? (
          <div className="member-table">
            <div className="mt-head six"><span>TANGGAL</span><span>AMOUNT</span><span>METODE</span><span>PERIODE MULAI</span><span>PERIODE AKHIR</span><span>CATATAN</span></div>
            {payments.map(p => (
              <div className="mt-row six" key={p.id} data-testid={`member-payment-${p.id}`}>
                <span>{(p.created_at || "").slice(0, 10)}</span>
                <b>{money(p.amount)}</b>
                <span>{p.method}</span>
                <span>{p.period_start}</span>
                <span>{p.period_end}</span>
                <span>{p.notes || "-"}</span>
              </div>
            ))}
          </div>
        ) : <p className="empty">Belum ada pembayaran tercatat.</p>}
      </div>
    </div>
  );
}

// ---------------- MODALS ----------------
function LoginModal({ close, onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("staff");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setErr(""); setLoading(true);
    try { await onLogin(email, password, mode); }
    catch (e) { setErr(e.response?.data?.detail || "Gagal login"); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <button className="modal-close" data-testid="login-modal-close" onClick={close}><X size={18} /></button>
        <span className="eyebrow">SIGN IN · CASSIA FREEDOM</span><h2>Selamat datang</h2>
        <div className="mode-toggle">
          <button className={mode === "staff" ? "active" : ""} data-testid="login-mode-staff" onClick={() => setMode("staff")}>Staff (Admin / Funder)</button>
          <button className={mode === "member" ? "active" : ""} data-testid="login-mode-member" onClick={() => setMode("member")}>Member</button>
        </div>
        <p>{mode === "staff" ? "Masuk ke Admin desk atau Funder vault." : "Masuk ke portal member — cek statistik & histori."}</p>
        <label>Email<input data-testid="login-email-input" value={email} onChange={e => setEmail(e.target.value)} placeholder={mode === "staff" ? "admin@cassiafreedom.id" : "kamu@email.com"} /></label>
        <label>Password<input type="password" data-testid="login-password-input" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" onKeyDown={e => e.key === "Enter" && submit()} /></label>
        {err && <div className="err-msg" data-testid="login-error">{err}</div>}
        <button className="primary full" data-testid="login-submit-button" disabled={loading || !email || !password} onClick={submit}>{loading ? "Masuk..." : "Sign in"} <KeyRound size={16} /></button>
      </div>
    </div>
  );
}

function ProfileModal({ profile, setProfile, close, save }) {
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <button className="modal-close" data-testid="profile-modal-close-button" onClick={close}><X size={18} /></button>
        <span className="eyebrow">YOUR PLAY PROFILE</span><h2>Ready to play?</h2>
        <p>Isi data singkat agar kamu bisa join room.</p>
        <label>Nama panggilan<input data-testid="profile-name-input" value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })} placeholder="Contoh: Aditya" /></label>
        <label>Nomor WhatsApp<input data-testid="profile-phone-input" value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })} placeholder="08xxxxxxxxxx" /></label>
        <label>Status<select data-testid="profile-membership-select" value={profile.membership} onChange={e => setProfile({ ...profile, membership: e.target.value })}>
          <option>Player</option><option>Reguler</option><option>Star</option><option>Guest</option>
        </select></label>
        <button className="primary full" data-testid="profile-save-button" onClick={save}>Simpan profil <Check size={17} /></button>
      </div>
    </div>
  );
}

function RoomModal({ profile, close, submit }) {
  const [form, setForm] = useState({
    host_name: profile.name || "",
    date: todayISO(),
    time: "07:30",
    level: "Friendly",
    capacity: 4,
    format: "Americano",
    scoring: "Best of 4",
  });
  const meta = CAPACITIES.find(c => c.value === form.capacity);
  return (
    <div className="modal-backdrop">
      <div className="modal wide">
        <button className="modal-close" data-testid="room-modal-close-button" onClick={close}><X size={18} /></button>
        <span className="eyebrow">CREATE A SESSION</span><h2>Open play room</h2>
        <p>Atur sesi favoritmu. Pilih kapasitas dan format bermain.</p>
        <div className="grid-2">
          <label>Host name<input data-testid="room-host_name-input" value={form.host_name} onChange={e => setForm({ ...form, host_name: e.target.value })} placeholder="Nama host" /></label>
          <label>Play date<input type="date" min={todayISO()} data-testid="room-date-input" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></label>
          <label>Start time<input type="time" data-testid="room-time-input" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} /></label>
          <label>Level<select data-testid="room-level-select" value={form.level} onChange={e => setForm({ ...form, level: e.target.value })}>
            {LEVELS.map(l => <option key={l}>{l}</option>)}
          </select></label>
          <label className="span-2">Kapasitas & durasi<select data-testid="room-capacity-select" value={form.capacity} onChange={e => setForm({ ...form, capacity: parseInt(e.target.value) })}>
            {CAPACITIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select></label>
          <label>Format<select data-testid="room-format-select" value={form.format} onChange={e => setForm({ ...form, format: e.target.value })}>
            {FORMATS.map(f => <option key={f}>{f}</option>)}
          </select></label>
          <label>Sistem skor<select data-testid="room-scoring-select" value={form.scoring} onChange={e => setForm({ ...form, scoring: e.target.value })}>
            {SCORES.map(s => <option key={s}>{s}</option>)}
          </select></label>
        </div>
        <div className="cap-note">Butuh <b>{meta.courts} court</b> selama <b>{meta.duration} menit</b>.</div>
        <button className="primary full" data-testid="room-submit-button" disabled={!form.host_name} onClick={() => submit(form)}>Buka room <Plus size={17} /></button>
      </div>
    </div>
  );
}

function MemberModal({ initial, close, submit }) {
  const [form, setForm] = useState(initial ? { ...initial, password: "" } : { name: "", phone: "", email: "", password: "", plan: "player", notes: "", active: true, referrer_code: "" });
  const clean = () => {
    const out = { ...form };
    if (!out.password) delete out.password;
    if (!out.email) delete out.email;
    if (!out.referrer_code) delete out.referrer_code;
    return out;
  };
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <button className="modal-close" data-testid="member-modal-close" onClick={close}><X size={18} /></button>
        <span className="eyebrow">{initial ? "EDIT MEMBER" : "NEW MEMBER"}</span><h2>{initial ? "Ubah data member" : "Tambah member baru"}</h2>
        <p>Isi email + password agar member bisa login ke portal statistiknya.</p>
        <label>Nama<input data-testid="member-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></label>
        <label>Nomor WhatsApp<input data-testid="member-phone-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></label>
        <label>Email<input data-testid="member-email-input" type="email" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="member@email.com" /></label>
        <label>Password {initial && <small style={{color:"#8b9b98"}}>(kosongkan jika tidak ingin ubah)</small>}<input data-testid="member-password-input" type="password" value={form.password || ""} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={initial ? "••••••••" : "Password portal member"} /></label>
        <label>Plan<select data-testid="member-plan-select" value={form.plan} onChange={e => setForm({ ...form, plan: e.target.value })}>
          <option value="player">Player</option><option value="reguler">Reguler</option><option value="star">Star</option>
        </select></label>
        {initial && <label>Status<select data-testid="member-active-select" value={form.active ? "1" : "0"} onChange={e => setForm({ ...form, active: e.target.value === "1" })}>
          <option value="1">Aktif</option><option value="0">Nonaktif</option>
        </select></label>}
        {!initial && <label>Kode referral (opsional)<input data-testid="member-referrer-input" value={form.referrer_code || ""} onChange={e => setForm({ ...form, referrer_code: e.target.value.toUpperCase() })} placeholder="Contoh: ABC123" /></label>}
        <label>Catatan<input data-testid="member-notes-input" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Opsional" /></label>
        <button className="primary full" data-testid="member-submit-button" disabled={!form.name || !form.phone} onClick={() => submit(clean())}>Simpan <Check size={17} /></button>
      </div>
    </div>
  );
}

function PaymentModal({ member, close, submit }) {
  const start = todayISO();
  const endDate = new Date(); endDate.setMonth(endDate.getMonth() + 1);
  const [form, setForm] = useState({ member_id: member.id, amount: 1000000, method: "Transfer", period_start: start, period_end: endDate.toISOString().slice(0, 10), notes: "" });
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <button className="modal-close" data-testid="payment-modal-close" onClick={close}><X size={18} /></button>
        <span className="eyebrow">CATAT PEMBAYARAN</span><h2>{member.name}</h2>
        <p>Log pembayaran manual untuk member ini.</p>
        <label>Nominal<input type="number" data-testid="payment-amount-input" value={form.amount} onChange={e => setForm({ ...form, amount: parseInt(e.target.value) || 0 })} /></label>
        <label>Metode<select data-testid="payment-method-select" value={form.method} onChange={e => setForm({ ...form, method: e.target.value })}>
          <option>Transfer</option><option>Cash</option><option>QRIS</option><option>Kartu</option>
        </select></label>
        <label>Periode mulai<input type="date" data-testid="payment-start-input" value={form.period_start} onChange={e => setForm({ ...form, period_start: e.target.value })} /></label>
        <label>Periode selesai<input type="date" data-testid="payment-end-input" value={form.period_end} onChange={e => setForm({ ...form, period_end: e.target.value })} /></label>
        <label>Catatan<input data-testid="payment-notes-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Opsional" /></label>
        <button className="primary full" data-testid="payment-submit-button" onClick={() => submit(form)}>Simpan pembayaran <Check size={17} /></button>
      </div>
    </div>
  );
}

function PriceModal({ plan, close, submit }) {
  const [price, setPrice] = useState(plan.price);
  const [applyNow, setApplyNow] = useState(false);
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <button className="modal-close" data-testid="price-modal-close" onClick={close}><X size={18} /></button>
        <span className="eyebrow">UBAH HARGA · {plan.name.toUpperCase()}</span><h2>Set harga baru</h2>
        <p>Berlaku sekarang (semua transaksi selanjutnya) atau tunggu billing berikutnya.</p>
        <label>Harga baru (Rp)<input type="number" data-testid="price-value-input" value={price} onChange={e => setPrice(parseInt(e.target.value) || 0)} /></label>
        <label className="check-row"><input type="checkbox" data-testid="price-applynow-checkbox" checked={applyNow} onChange={e => setApplyNow(e.target.checked)} /> Berlaku sekarang</label>
        <button className="primary full" data-testid="price-submit-button" onClick={() => submit({ price, apply_now: applyNow })}>Simpan harga <Check size={17} /></button>
      </div>
    </div>
  );
}

function AdModal({ close, submit }) {
  const [form, setForm] = useState({ title: "", subtitle: "", cta: "Lihat promo", active: true });
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <button className="modal-close" data-testid="ad-modal-close" onClick={close}><X size={18} /></button>
        <span className="eyebrow">NEW CAMPAIGN</span><h2>Tambah iklan</h2>
        <p>Iklan tampil di halaman Overview member.</p>
        <label>Judul<input data-testid="ad-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></label>
        <label>Subtitle<input data-testid="ad-subtitle-input" value={form.subtitle} onChange={e => setForm({ ...form, subtitle: e.target.value })} /></label>
        <label>CTA<input data-testid="ad-cta-input" value={form.cta} onChange={e => setForm({ ...form, cta: e.target.value })} /></label>
        <button className="primary full" data-testid="ad-submit-button" disabled={!form.title} onClick={() => submit(form)}>Tayangkan <Check size={17} /></button>
      </div>
    </div>
  );
}

function SettingsPanel({ initial, onReload, showToast }) {
  const [form, setForm] = useState({ total_courts: initial.total_courts, whatsapp_group: initial.whatsapp_group, guest_price: initial.guest_price });
  const save = async () => {
    try {
      await axios.patch(`${API}/settings`, form, { headers: authHeader() });
      showToast("Setting tersimpan"); onReload();
    } catch { showToast("Gagal simpan setting"); }
  };
  return (
    <div className="admin-section">
      <div className="section-head"><div><span className="eyebrow">CLUB SETTINGS</span><h2>Konfigurasi club</h2></div></div>
      <div className="settings-grid">
        <label>Total court tersedia<input type="number" min="1" data-testid="settings-courts-input" value={form.total_courts} onChange={e => setForm({ ...form, total_courts: parseInt(e.target.value) || 1 })} /></label>
        <label>Harga guest (Rp/sesi)<input type="number" data-testid="settings-guest-price-input" value={form.guest_price} onChange={e => setForm({ ...form, guest_price: parseInt(e.target.value) || 0 })} /></label>
        <label className="span-2">Link WhatsApp group member<input data-testid="settings-wa-group-input" value={form.whatsapp_group || ""} onChange={e => setForm({ ...form, whatsapp_group: e.target.value })} placeholder="https://chat.whatsapp.com/..." /></label>
      </div>
      <button className="primary" data-testid="settings-save-button" onClick={save}>Simpan setting <Check size={17} /></button>
    </div>
  );
}

function ResultModal({ room, close, submit }) {
  const players = room.players || [];
  const [winners, setWinners] = useState([]);
  const [mvp, setMvp] = useState("");
  const [commentary, setCommentary] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const toggle = (name) => {
    setWinners(w => {
      const next = w.includes(name) ? w.filter(x => x !== name) : [...w, name];
      if (mvp && !next.includes(mvp)) setMvp("");
      return next;
    });
  };
  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await axios.get(`${API}/rooms/${room.id}/suggest-teams`, { headers: authHeader() });
      setSuggestion(data);
      setWinners(data.team_a.map(p => p.name));
    } catch { /* silent */ }
    finally { setSuggesting(false); }
  };
  const doSubmit = () => submit({ winners, mvp: mvp || null, commentary: commentary || null });
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <button className="modal-close" data-testid="result-modal-close" onClick={close}><X size={18} /></button>
        <span className="eyebrow">RECORD MATCH RESULT</span><h2>{room.host_name}'s room</h2>
        <p>{room.date} · {room.time} · {room.format} · {room.scoring} — pilih tim yang menang. Selain itu otomatis kalah.</p>
        <button className="secondary tiny suggest-btn" data-testid="result-suggest-button" disabled={suggesting} onClick={suggest}>
          <Sparkles size={13} /> {suggesting ? "Menghitung..." : "Suggest fair teams (snake draft)"}
        </button>
        {suggestion && (
          <div className="suggest-note" data-testid="suggest-note">
            Balance: Team A {suggestion.team_a_wins} wins · Team B {suggestion.team_b_wins} wins (dari histori).
          </div>
        )}
        <div className="result-players">
          {players.map(p => (
            <label key={p.name} className={`result-row ${winners.includes(p.name) ? "win" : ""} ${mvp === p.name ? "mvp" : ""}`} data-testid={`result-toggle-${p.name}`}>
              <input type="checkbox" checked={winners.includes(p.name)} onChange={() => toggle(p.name)} />
              <div className="face">{p.name.slice(0,1)}</div>
              <b>{p.name}</b>
              {p.is_guest && <small>guest</small>}
              {winners.includes(p.name) && (
                <button type="button" className={`mvp-btn ${mvp === p.name ? "active" : ""}`} data-testid={`result-mvp-${p.name}`} onClick={(e) => { e.preventDefault(); setMvp(mvp === p.name ? "" : p.name); }}>
                  <Star size={12} /> MVP
                </button>
              )}
              <span className="tag">{winners.includes(p.name) ? "MENANG" : "KALAH"}</span>
            </label>
          ))}
        </div>
        <label className="commentary-label">
          <MessageCircle size={12} style={{marginRight:6}} /> Commentary (opsional)
          <textarea data-testid="result-commentary-input" rows="2" value={commentary} onChange={e => setCommentary(e.target.value)} placeholder="Tulis cerita singkat pertandingan (contoh: 'Comeback dramatis 5-5, tiebreak seru!')" />
        </label>
        <button className="primary full" data-testid="result-submit-button" disabled={!winners.length || winners.length === players.length} onClick={doSubmit}>
          Simpan hasil <Trophy size={17} />
        </button>
        {winners.length === players.length && <div className="err-msg">Minimal 1 pemain harus kalah.</div>}
      </div>
    </div>
  );
}

export default App;
